import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { Package, AlertTriangle, Search, ChevronDown, ChevronUp, Barcode, Download, Printer, Check, X, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import BarcodeScanInput from '../components/BarcodeScanInput';

const PAGE_SIZE = 50;

const emptyItem = {
  name: '', sku: '', category: '', description: '', unit_price: '', cost_price: '',
  normal_price: '', wholesale_price: '', barcode: '', uom: '', quantity_on_hand: '',
  reorder_point: '10', is_taxable: true, last_vendor: ''
};

const Inventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('product_name');
  const [sortDir, setSortDir] = useState('asc');
  const [barcodeModal, setBarcodeModal] = useState(null);
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ ...emptyItem });
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (currentPage - 1) * PAGE_SIZE, limit: PAGE_SIZE };
      if (filter === 'low') params.low_stock_only = true;
      if (search.trim()) params.search = search.trim();
      const data = await api.getInventory(params);
      setInventory(data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, search, currentPage]);

  const loadCount = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter === 'low') params.append('low_stock_only', 'true');
      if (search.trim()) params.append('search', search.trim());
      const res = await api.request(`/api/inventory/count?${params.toString()}`);
      setTotalCount(res?.total || 0);
    } catch (e) { console.error('Failed to load inventory count:', e); }
  }, [filter, search]);

  // Reset to page 1 whenever filter/search changes
  useEffect(() => { setCurrentPage(1); }, [filter, search]);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { loadCount(); }, [loadCount]);

  const toggleTaxable = async (item) => {
    const newVal = !item.is_taxable;
    try {
      await api.updateProductTaxable(item.product_id, newVal);
      setInventory(prev => prev.map(i =>
        i.id === item.id ? { ...i, is_taxable: newVal } : i
      ));
    } catch (_err) {
      alert('Failed to update tax status');
    }
  };

  const generateBarcode = async (sku) => {
    setBarcodeLoading(true);
    try {
      const result = await api.generateBarcode(sku, 'code128');
      setBarcodeModal(result);
    } catch (err) {
      alert('Failed to generate barcode: ' + err.message);
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleDownload = (dataUrl, name) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `barcode-${name}.png`;
    link.click();
  };

  const handlePrint = (dataUrl) => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Print Barcode</title><style>
@page { size: 2.25in 0.75in; margin: 0; }
@media print { body { margin: 0; padding: 0; } }
body { display: flex; flex-direction: column; justify-content: center; align-items: center; width: 2.25in; height: 0.75in; margin: 0; padding: 2px; box-sizing: border-box; }
img { max-width: 2in; max-height: 0.5in; object-fit: contain; }
</style></head><body>
<img src="${dataUrl}" />
<script>window.onload=function(){window.print();window.close();}</script>
</body></html>`);
  };

  const handleAddItem = async () => {
    if (!form.name.trim()) { alert('Product name is required'); return; }
    setSaving(true);
    try {
      await api.addInventoryItem({
        ...form,
        unit_price: parseFloat(form.unit_price) || 0,
        cost_price: parseFloat(form.cost_price) || 0,
        normal_price: parseFloat(form.normal_price) || 0,
        wholesale_price: parseFloat(form.wholesale_price) || 0,
        quantity_on_hand: parseInt(form.quantity_on_hand) || 0,
        reorder_point: parseInt(form.reorder_point) || 10,
      });
      setShowAddForm(false);
      setForm({ ...emptyItem });
      loadInventory();
    } catch (err) {
      alert('Failed to add item: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...inventory].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const paginatedItems = sorted; // server-paginated
  const lowCount = inventory.filter(i => i.quantity_on_hand <= i.reorder_point).length;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Inventory</h2>
          <p className="text-muted-foreground">{totalCount.toLocaleString()} items{search && ' (filtered)'}{lowCount > 0 && ` · ${lowCount} low on this page`}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="add-item-btn">
            <Plus className="h-4 w-4" /> Add Item
          </button>
          <button onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${filter === 'all' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="filter-all">All Items</button>
          <button onClick={() => setFilter('low')}
            className={`px-4 py-2 rounded-md text-sm font-medium ${filter === 'low' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="filter-low">Low Stock</button>
        </div>
      </div>

      {/* Add Item Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-item-modal">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Inventory Item</h3>
              <button onClick={() => setShowAddForm(false)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Product Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">SKU</label>
                <input type="text" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})}
                  placeholder="Auto-generated if blank" className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-sku" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <input type="text" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-category" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit Price</label>
                <input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({...form, unit_price: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-price" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cost Price</label>
                <input type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({...form, cost_price: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-cost" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Normal Price</label>
                <input type="number" step="0.01" value={form.normal_price} onChange={(e) => setForm({...form, normal_price: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-normal-price" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Wholesale Price</label>
                <input type="number" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({...form, wholesale_price: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-wholesale" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantity On Hand</label>
                <input type="number" value={form.quantity_on_hand} onChange={(e) => setForm({...form, quantity_on_hand: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-qty" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reorder Point</label>
                <input type="number" value={form.reorder_point} onChange={(e) => setForm({...form, reorder_point: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-reorder" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Barcode</label>
                <input type="text" value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-barcode" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">UOM</label>
                <input type="text" value={form.uom} onChange={(e) => setForm({...form, uom: e.target.value})}
                  placeholder="e.g. each, ft, lb" className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-uom" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Vendor</label>
                <input type="text" value={form.last_vendor} onChange={(e) => setForm({...form, last_vendor: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md" data-testid="add-vendor" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md h-16" data-testid="add-description" />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.is_taxable ? 'bg-blue-500' : 'bg-gray-300'}`}
                    onClick={() => setForm({...form, is_taxable: !form.is_taxable})} data-testid="add-taxable-toggle">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.is_taxable ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className="text-sm font-medium">{form.is_taxable ? 'Sales Taxable' : 'Not Taxable'}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-input rounded-md hover:bg-muted" data-testid="cancel-add-btn">Cancel</button>
              <button onClick={handleAddItem} disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-item-btn">
                {saving ? 'Saving...' : 'Add to Inventory'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 max-w-3xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Search by name, SKU, or category..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-input rounded-md" data-testid="inventory-search" />
        </div>
        {/* Scan a barcode → jump straight to that product's detail page */}
        <div className="flex-1">
          <BarcodeScanInput
            onScan={(product) => {
              if (product?.product_id) navigate(`/products/${product.product_id}`);
              else if (product?.sku) setSearch(product.sku);
            }}
            placeholder="Scan barcode → open product"
            autoFocus={false}
          />
        </div>
      </div>

      {/* Barcode Modal */}
      {barcodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="barcode-modal">
          <div className="bg-card rounded-lg border border-border p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Barcode: {barcodeModal.sku}</h3>
              <button onClick={() => setBarcodeModal(null)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="bg-white p-6 rounded-lg border text-center">
              <img src={barcodeModal.image_data_url} alt={`Barcode ${barcodeModal.sku}`} className="max-w-full mx-auto" data-testid="barcode-image" />
            </div>
            <p className="text-center text-sm text-muted-foreground mt-2">Code 128 - Scannable by phone cameras and USB barcode scanners</p>
            <div className="flex justify-center gap-3 mt-4">
              <button onClick={() => handleDownload(barcodeModal.image_data_url, barcodeModal.sku)}
                className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-muted" data-testid="barcode-download">
                <Download className="h-4 w-4" /> Download
              </button>
              <button onClick={() => handlePrint(barcodeModal.image_data_url)}
                className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-muted" data-testid="barcode-print">
                <Printer className="h-4 w-4" /> Print
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('product_name')}>
                  Product <SortIcon field="product_name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('sku')}>
                  SKU <SortIcon field="sku" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('category')}>
                  Category <SortIcon field="category" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('normal_price')}>
                  Regular Price <SortIcon field="normal_price" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('unit_price')}>
                  PW OIL Price <SortIcon field="unit_price" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('cost_price')}>
                  Cost <SortIcon field="cost_price" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('quantity_on_hand')}>
                  On Hand <SortIcon field="quantity_on_hand" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Reorder Pt</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Taxable</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Barcode</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="11" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="11" className="px-4 py-8 text-center text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />No inventory items found
                </td></tr>
              ) : (
                paginatedItems.map((item) => {
                  const isLow = item.quantity_on_hand <= item.reorder_point;
                  return (
                    <tr key={item.id} className={`hover:bg-muted/30 ${isLow ? 'bg-yellow-50/50' : ''}`} data-testid={`inv-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/products/${item.product_id}`} className="text-sm font-medium text-primary hover:underline">{item.product_name}</Link>
                        {item.last_vendor && <p className="text-xs text-muted-foreground">{item.last_vendor}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{item.sku}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{item.category || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums font-medium">${(item.normal_price || item.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-amber-700 font-medium">${(item.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-muted-foreground">${(item.cost_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold tabular-nums ${isLow ? 'text-red-600' : ''}`}>{item.quantity_on_hand}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-muted-foreground">{item.reorder_point}</td>
                      <td className="px-4 py-3 text-center">
                        {isLow ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                            <AlertTriangle className="h-3 w-3" /> Low
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">In Stock</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleTaxable(item)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                            item.is_taxable !== false ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`} data-testid={`tax-toggle-${item.id}`}>
                          {item.is_taxable !== false ? <><Check className="h-3 w-3" /> Tax</> : 'No Tax'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => generateBarcode(item.sku)}
                          disabled={barcodeLoading}
                          className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                          title="Generate barcode" data-testid={`barcode-btn-${item.id}`}>
                          <Barcode className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30" data-testid="inventory-pagination">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} items
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="inv-page-first">First</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="inv-page-prev">
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      currentPage === page ? 'bg-primary text-white border-primary' : 'border-input hover:bg-muted'
                    }`} data-testid={`inv-page-${page}`}>{page}</button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="inv-page-next">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="inv-page-last">Last</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
